import { Button, Input, Tab } from '@features/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ProductAccountTypes,
  ProductOffer,
  ProductTypes,
  TOption,
  TTab,
} from '@src/types';
import { platformRadioData } from '@features/admin/packages/packagesData';
import { Addons, FilterData, Products } from '@features/admin/productsAddons';
import { useNavigate } from 'react-router-dom';
import {
  getProductOptions,
  getProducts,
  saveAddonsAssociations,
  saveBundlesNumberInAccount,
  saveDisabledAddons,
} from '@src/services/products.ts';
import queryString from 'query-string';
import { Bundles } from '@features/admin/productsAddons/Bundles.tsx';
import { ViewAccountPopup } from '@features/admin/productsAddons/ViewAccountPopup.tsx';
import { useProductOptions } from '@hooks/useProductOptions.tsx';
import { ConfirmDisabledPopup } from '@features/admin/productsAddons/ConfirmDisabledPopup.tsx';
import { ProductReset } from '@features/admin/productsAddons/ProductReset.tsx';
import { ProductMarketData } from '@features/admin/productsAddons/ProductMarketData.tsx';
import { ProductOtherData } from '@features/admin/productsAddons/ProductOtherData.tsx';

export const ProductsAddonsPage = () => {
  const [tabActive, setTabActive] = useState(0);
  const [productRadioSelected, setProductRadioSelected] = useState<string>('');
  const [addonRadioSelected, setAddonRadioSelected] = useState<string>('');
  const [bundleRadioSelected, setBundleRadioSelected] = useState<string>('');
  const [resetRadioSelected, setResetRadioSelected] = useState<string>('');
  const [marketDataRadioSelected, setMarketDataRadioSelected] =
    useState<string>('');
  const [accountsSelected, setAccountsSelected] = useState<number[]>([]);
  const [numberOfAccounts, setNumberOfAccounts] = useState<number>();
  const [products, setProducts] = useState<ProductOffer[]>([]);
  const [productAddons, setProductAddons] = useState<ProductOffer[]>([]);
  const [productBundles, setProductBundles] = useState<ProductOffer[]>([]);
  const [productResets, setProductResets] = useState<ProductOffer[]>([]);
  const [productMarketData, setProductMarketData] = useState<ProductOffer[]>(
    []
  );
  const [productOtherData, setProductOtherData] = useState<ProductOffer[]>([]);
  const [bundleSelectedIds, setBundleSelectedIds] = useState<number[]>([]);
  const [addonsSelectedIds, setAddonsSelectedIds] = useState<number[]>([]);
  const [addonsDisableIds, setAddonsDisableIds] = useState<number[]>([]);
  const [addonPackages, setAddonPackages] = useState<number[]>([]);
  const [viewAccount, setViewAccount] = useState(false);
  const [confirmDisabled, setConfirmDisabled] = useState(false);
  const [productAddonOptions, setProductAddonOptions] = useState<TOption[]>([]);

  const navigate = useNavigate();
  const { productOptions } = useProductOptions();

  const fetchProductOptions = useCallback(async () => {
    const res = await getProductOptions({
      categories: [
        ProductTypes.TRADING_ACCOUNT,
        ProductTypes.RESETS,
        ProductTypes.TRADING_ACCOUNT,
      ],
    });
    const options = res.data.map((item) => ({
      label: item.title,
      value: item.id,
    }));

    setProductAddonOptions(options as unknown as TOption[]);
  }, []);

  useEffect(() => {
    fetchProductOptions().then();
  }, [fetchProductOptions]);

  const fetchProducts = useCallback(async (productRadioSelected?: string) => {
    const productRes = await getProducts({
      categories: ProductTypes.TRADING_ACCOUNT,
      datafeed:
        productRadioSelected !== 'all' ? productRadioSelected : undefined,
    });

    setProducts(productRes.data);
  }, []);

  const fetchProductAddons = useCallback(async (platform?: string) => {
    const productRes = await getProducts({
      categories: ProductTypes.ADD_ONS_EDGE_ENHANCERS,
      datafeed: platform !== 'all' ? platform : undefined,
    });

    setProductAddons(productRes.data);
  }, []);

  const fetchProductBundles = useCallback(async (platform?: string) => {
    const productRes = await getProducts({
      categories: ProductTypes.TRADING_ACCOUNT,
      account_types: [ProductAccountTypes.BUNDLE],
      datafeed: platform !== 'all' ? platform : undefined,
    });

    setProductBundles(productRes.data);
  }, []);

  const fetchProductResets = useCallback(async (platform?: string) => {
    const productRes = await getProducts({
      categories: ProductTypes.RESETS,
      datafeed: platform !== 'all' ? platform : undefined,
    });

    setProductResets(productRes.data);
  }, []);

  const fetchProductMarketData = useCallback(async (platform?: string) => {
    const productRes = await getProducts({
      categories: ProductTypes.MARKET_DATA,
      datafeed: platform !== 'all' ? platform : undefined,
    });

    setProductMarketData(productRes.data);
  }, []);

  const fetchProductOtherData = useCallback(async (platform?: string) => {
    const productRes = await getProducts({
      categories: ProductTypes.OTHER,
      datafeed: platform !== 'all' ? platform : undefined,
    });

    setProductOtherData(productRes.data);
  }, []);

  useEffect(() => {
    fetchProducts().then();
    fetchProductAddons().then();
    fetchProductBundles().then();
    fetchProductResets().then();
    fetchProductMarketData().then();
    fetchProductOtherData().then();
  }, [
    fetchProductAddons,
    fetchProductBundles,
    fetchProductMarketData,
    fetchProductResets,
    fetchProducts,
    fetchProductOtherData,
  ]);

  const handleEditProduct = useCallback(
    (id: number) => {
      navigate(`/admin/products-addons/product/${id}`);
    },
    [navigate]
  );

  const handleEditReset = useCallback(
    (id: number) => {
      navigate(`/admin/products-addons/reset/${id}`);
    },
    [navigate]
  );
  const handleEditMarketData = useCallback(
    (id: number) => {
      navigate(`/admin/products-addons/market-data/${id}`);
    },
    [navigate]
  );

  const handleCopyProduct = useCallback(
    (id: number) => {
      const params = {
        templateProductId: id,
      };
      const searchString = queryString.stringify(params);

      navigate({
        pathname: '/admin/products-addons/product/create',
        search: searchString,
      });
    },
    [navigate]
  );

  const handleEditAddons = useCallback(
    (id: number) => {
      navigate(`/admin/products-addons/product/${id}`);
    },
    [navigate]
  );
  const handleConfirmDisabledAddons = useCallback(async () => {
    await saveDisabledAddons(addonsDisableIds);
    await fetchProductAddons(addonRadioSelected);
    setAddonsDisableIds([]);
  }, [addonRadioSelected, addonsDisableIds, fetchProductAddons]);

  const handleViewAccount = useCallback((ids: number[]) => {
    setAddonPackages(ids);
    setViewAccount(true);
  }, []);

  const handleFilterProduct = useCallback(
    async (value: string) => {
      setProductRadioSelected(value);
      await fetchProducts(value);
    },
    [fetchProducts]
  );

  const handleFilterAddons = useCallback(
    async (value: string) => {
      setAddonRadioSelected(value);
      await fetchProductAddons(value);
    },
    [fetchProductAddons]
  );

  const handleFilterBundles = useCallback(
    async (value: string) => {
      setBundleRadioSelected(value);
      await fetchProductBundles(value);
    },
    [fetchProductBundles]
  );

  const handleFilterReset = useCallback(
    async (value: string) => {
      setResetRadioSelected(value);
      await fetchProductResets(value);
    },
    [fetchProductResets]
  );

  const handleMarketDataReset = useCallback(
    async (value: string) => {
      setMarketDataRadioSelected(value);
      await fetchProductMarketData(value);
    },
    [fetchProductMarketData]
  );

  const handleApplyAddons = useCallback(async () => {
    await saveAddonsAssociations({
      addon_ids: addonsSelectedIds,
      account_ids: accountsSelected,
    });
    await fetchProductAddons(addonRadioSelected);
    setAddonsSelectedIds([]);
    setAccountsSelected([]);
  }, [
    accountsSelected,
    addonRadioSelected,
    addonsSelectedIds,
    fetchProductAddons,
  ]);

  const handleApplyBundles = useCallback(async () => {
    await saveBundlesNumberInAccount({
      bundle_ids: bundleSelectedIds,
      accounts_in_bundle: numberOfAccounts,
    });
    await fetchProductBundles(bundleRadioSelected);
    setBundleSelectedIds([]);
    setNumberOfAccounts(undefined);
  }, [
    bundleRadioSelected,
    bundleSelectedIds,
    fetchProductBundles,
    numberOfAccounts,
  ]);

  const tabs = useMemo(
    (): TTab[] => [
      {
        label: 'Product',
        header: (
          <FilterData
            options={platformRadioData}
            radioSelected={productRadioSelected}
            setRadioSelected={handleFilterProduct}
            title={'Products'}
          />
        ),
        component: (
          <Products
            data={products}
            onCopy={handleCopyProduct}
            onEdit={handleEditProduct}
          />
        ),
      },
      {
        label: 'Addons',
        header: (
          <FilterData
            options={platformRadioData}
            radioSelected={addonRadioSelected}
            setRadioSelected={handleFilterAddons}
            title={'Addons'}
          />
        ),
        component: (
          <>
            <div className={'flex justify-end'}>
              {addonsDisableIds.length ? (
                <Button
                  size={'sm'}
                  variant={'lime'}
                  onClick={() => setConfirmDisabled(true)}
                >
                  Save
                </Button>
              ) : null}
            </div>
            <Addons
              addonsDisableIds={addonsDisableIds}
              addonsSelectedIds={addonsSelectedIds}
              data={productAddons}
              setAddonsDisableIds={setAddonsDisableIds}
              setAddonsSelectedIds={setAddonsSelectedIds}
              onEdit={handleEditAddons}
              onView={handleViewAccount}
            />
          </>
        ),
      },
      {
        label: 'Bundles',
        header: (
          <FilterData
            options={platformRadioData}
            radioSelected={bundleRadioSelected}
            setRadioSelected={handleFilterBundles}
            title={'Bundles'}
          />
        ),
        component: (
          <Bundles
            bundleSelectedIds={bundleSelectedIds}
            data={productBundles}
            setBundleSelectedIds={setBundleSelectedIds}
            onEdit={handleEditProduct}
          />
        ),
      },
      {
        label: 'Reset',
        header: (
          <FilterData
            options={platformRadioData}
            radioSelected={resetRadioSelected}
            setRadioSelected={handleFilterReset}
            title={'Reset'}
          />
        ),
        component: (
          <ProductReset data={productResets} onEdit={handleEditReset} />
        ),
      },
      {
        label: 'Market Data',
        header: (
          <FilterData
            options={platformRadioData}
            radioSelected={marketDataRadioSelected}
            setRadioSelected={handleMarketDataReset}
            title={'Market Data'}
          />
        ),
        component: (
          <ProductMarketData
            data={productMarketData}
            onEdit={handleEditMarketData}
          />
        ),
      },
      {
        label: 'Other',
        component: (
          <ProductOtherData
            data={productOtherData}
            onEdit={handleEditProduct}
          />
        ),
      },
    ],
    [
      productRadioSelected,
      handleFilterProduct,
      products,
      handleCopyProduct,
      handleEditProduct,
      addonRadioSelected,
      handleFilterAddons,
      addonsDisableIds,
      addonsSelectedIds,
      productAddons,
      handleEditAddons,
      handleViewAccount,
      bundleRadioSelected,
      handleFilterBundles,
      bundleSelectedIds,
      productBundles,
      resetRadioSelected,
      handleFilterReset,
      productResets,
      handleEditReset,
      marketDataRadioSelected,
      handleMarketDataReset,
      productMarketData,
      handleEditMarketData,
      productOtherData,
    ]
  );

  const renderHeaderAction = useCallback(() => {
    switch (tabActive) {
      case 0:
        return (
          <Button
            className={'w-fit'}
            size={'sm'}
            variant={'lime'}
            onClick={() => navigate('/admin/products-addons/product/create')}
          >
            Create New Product
          </Button>
        );
      case 1:
        return <div className={'flex items-center gap-7'} />;
      case 2:
        return (
          <div className={'flex items-center gap-7'}>
            <div className={'w-[351px] text-xs'}>
              <Input
                placeholder={'Enter Number of accounts'}
                textSize={'xs'}
                type={'number'}
                value={numberOfAccounts}
                variant={'secondary'}
                onChange={(event) =>
                  setNumberOfAccounts(Number(event.target.value))
                }
              />
            </div>
            <Button
              size={'sm'}
              variant={'secondary'}
              onClick={handleApplyBundles}
            >
              Apply
            </Button>
          </div>
        );
      default:
        return <></>;
    }
  }, [
    accountsSelected,
    handleApplyAddons,
    handleApplyBundles,
    navigate,
    numberOfAccounts,
    productAddonOptions,
    tabActive,
  ]);

  return (
    <div>
      <div
        className={
          'flex lg:flex-row flex-col justify-between lg:items-center gap-2'
        }
      >
        <h1 className={'text-[23px]'}>Products</h1>
        <div className={'flex lg:flex-row flex-col gap-[56px]'}>
          {renderHeaderAction()}
        </div>
      </div>
      <div className={'relative lg:mt-4.5 mt-2'}>
        <Tab
          className={'pb-2 pl-5'}
          size={'lg'}
          tabActive={tabActive}
          tabs={tabs}
          onChangeTab={setTabActive}
        />
      </div>
      <ViewAccountPopup
        open={viewAccount}
        productOptions={productOptions.filter((item) =>
          addonPackages.includes(Number(item.value))
        )}
        onOpenChange={setViewAccount}
      />
      <ConfirmDisabledPopup
        open={confirmDisabled}
        onConfirm={handleConfirmDisabledAddons}
        onOpenChange={setConfirmDisabled}
      />
    </div>
  );
};
